import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Icon, Badge, Button, SectionHead } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';

const INTERESTS = [
  { id: 'Temples', icon: 'temple' },
  { id: 'Historical Sites', icon: 'landmark' },
  { id: 'Spiritual Activities', icon: 'diya' },
  { id: 'Cultural Experience', icon: 'sparkles' },
  { id: 'Local Cuisine', icon: 'utensils' },
];
const DURATIONS = [1, 2, 3, 4, 5];

const STOP_BY_INTEREST = {
  Temples: ['Shri Ram Janmabhoomi Mandir', 'Hanuman Garhi', 'Kanak Bhawan'],
  'Historical Sites': ['Dashrath Mahal', 'Gulab Bari', 'Mani Parbat'],
  'Spiritual Activities': ['Saryu aarti at Ram ki Paidi', 'Guptar Ghat', 'Nageshwarnath Temple'],
  'Cultural Experience': ['Ram Katha Park', 'Ram Leela performance', 'Tulsi Smarak Bhawan'],
  'Local Cuisine': ['Awadhi thali on Ram Path', 'Sattvik bhojanalaya near Hanuman Garhi', 'Prasad & pedha shopping'],
};

const POPULAR_ROUTES = [
  { id: 1, name: 'Temple Circuit', desc: 'Visit the most important temples in Ayodhya', duration: '6–8 hours', stops: ['Ram Mandir', 'Hanuman Garhi', 'Kanak Bhawan', 'Nageshwarnath Temple'] },
  { id: 2, name: 'Spiritual Walk', desc: 'A peaceful walk along the Saryu river and nearby temples', duration: '3–4 hours', stops: ['Saryu Ghat', 'Ram ki Paidi', 'Treta ke Thakur', 'Sita ki Rasoi'] },
  { id: 3, name: 'Heritage Trail', desc: 'Palaces, gardens and the older quarters of the city', duration: '5–6 hours', stops: ['Dashrath Mahal', 'Gulab Bari', 'Bahu Begum ka Maqbara', 'Mani Parbat'] },
];

export default function TravelPlanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [params] = useSearchParams();

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date(); maxDate.setDate(maxDate.getDate() + 90);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const [arrivalDate, setArrivalDate] = useState(params.get('date') && params.get('date') >= today ? params.get('date') : '');
  const [duration, setDuration] = useState(Number(params.get('days')) || '');
  const [travellers, setTravellers] = useState(Number(params.get('travelers')) || 2);
  const [selectedInterests, setSelectedInterests] = useState(['Temples']);
  const [itinerary, setItinerary] = useState(null);
  const [toast, setToast] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const toggleInterest = (i) => setSelectedInterests((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  const handleGenerate = (e) => {
    e.preventDefault();
    const days = Number(duration) || 1;
    const pool = selectedInterests.flatMap((i) => STOP_BY_INTEREST[i] || []);
    const stops = (pool.length ? pool : STOP_BY_INTEREST.Temples).slice(0, Math.max(3, days * 3));
    setItinerary({ name: t('{{n}}-day custom itinerary', { n: days }), duration: days, arrivalDate, travellers, interests: selectedInterests, stops });
    setToast(t('Itinerary generated!'));
    setTimeout(() => setToast(''), 3000);
  };

  const handleSave = () => {
    if (!isAuthenticated) { setShowLoginPrompt(true); return; }
    setToast(t('Itinerary saved!'));
    setTimeout(() => setToast(''), 3000);
  };

  return (
    <>
      <SEO
        title="Trip Planner"
        description="Plan your Ayodhya pilgrimage with a custom itinerary. Choose your dates, interests and duration to get a day-by-day plan."
        canonicalUrl="/travel-planner"
        keywords="Ayodhya itinerary, Ayodhya trip planner, Ayodhya 2 day plan, plan Ayodhya visit"
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Travel Guide') }, { label: t('Trip Planner') }]} title={t('Trip Planner')} subtitle={t("Tell us when you're arriving and what interests you — we'll suggest the perfect itinerary")} />

      <div className="container content-wrap">
        <div className="content content--wide">
          <form onSubmit={handleGenerate}>
            <div className="card card--pad-lg">
              <SectionHead eyebrow={t('Your visit')} title={t('Your visit details')} as="h2" />

              <div className="form-grid">
                <label className="field">
                  <span className="field__label">{t('Arrival date')}</span>
                  <span className="control"><Icon name="calendar" size={16} />
                    <input type="date" value={arrivalDate} min={today} max={maxDateStr} onChange={(e) => setArrivalDate(e.target.value)} />
                  </span>
                </label>
                <label className="field">
                  <span className="field__label">{t('Travellers')}</span>
                  <span className="control control--select"><Icon name="users" size={16} />
                    <select value={travellers} onChange={(e) => setTravellers(Number(e.target.value))}>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </span>
                </label>
              </div>

              <div className="field" style={{ marginTop: 18 }}>
                <span className="field__label">{t('Duration of stay')}</span>
                <div className="slot-grid">
                  {DURATIONS.map((d) => (
                    <button key={d} type="button" className={`chip${duration === d ? ' is-active' : ''}`} onClick={() => setDuration(d)}>
                      {d}{d === 5 ? '+' : ''} {d === 1 ? t('day') : t('days')}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field" style={{ marginTop: 18 }}>
                <span className="field__label">{t('Interests')}</span>
                <div className="slot-grid">
                  {INTERESTS.map((i) => {
                    const active = selectedInterests.includes(i.id);
                    return (
                      <button key={i.id} type="button" className={`chip${active ? ' is-active' : ''}`} onClick={() => toggleInterest(i.id)} aria-pressed={active}>
                        <Icon name={active ? 'check' : i.icon} size={14} />{t(i.id)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginTop: 24 }}>
                <Button type="submit" block size="lg" icon={<Icon name="route" size={18} />} iconPosition="left">{t('Generate my itinerary')}</Button>
              </div>
            </div>
          </form>

          {itinerary && (
            <div className="card card--pad-lg planner__result" style={{ marginTop: 24 }}>
              <div className="planner__result-head">
                <div>
                  <span className="eyebrow">{t('Your itinerary')}</span>
                  <h2 className="h-section" style={{ fontSize: 24, marginTop: 6 }}>{itinerary.name}</h2>
                </div>
                <Button size="sm" variant="outline" onClick={handleSave} icon={<Icon name="bookmark" size={14} />} iconPosition="left">{t('Save')}</Button>
              </div>

              <div className="detail__meta" style={{ marginTop: 10 }}>
                {itinerary.arrivalDate && <span><Icon name="calendar" size={15} />{itinerary.arrivalDate}</span>}
                <span><Icon name="clock" size={15} />{itinerary.duration} {itinerary.duration === 1 ? t('day') : t('days')}</span>
                <span><Icon name="users" size={15} />{itinerary.travellers}</span>
              </div>

              <div className="field__label" style={{ margin: '18px 0 10px' }}>{t('Suggested stops')}</div>
              <ol className="itin__stops">
                {itinerary.stops.map((stop, i) => <li key={`${stop}-${i}`}>{stop}</li>)}
              </ol>

              <div className="hstack" style={{ marginTop: 20, gap: 10, flexWrap: 'wrap' }}>
                <Button size="sm" to="/darshan-booking" icon={<Icon name="temple" size={14} />} iconPosition="left">{t('Book Darshan')}</Button>
                <Button size="sm" variant="outline" to="/hotels" icon={<Icon name="bed-double" size={14} />} iconPosition="left">{t('Find a stay')}</Button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 44 }}>
            <SectionHead eyebrow={t('Ready-made plans')} title={t('Popular routes')} subtitle={t('Tried and tested circuits you can follow as they are')} />
            <div className="grid-3">
              {POPULAR_ROUTES.map((route) => (
                <article key={route.id} className="card info-card">
                  <div className="info-card__head">
                    <h3 className="info-card__title">{t(route.name)}</h3>
                    <Badge variant="outline"><Icon name="clock" size={13} />{route.duration}</Badge>
                  </div>
                  <p className="info-card__desc">{t(route.desc)}</p>
                  <div className="info-card__tags">
                    {route.stops.map((stop) => <Badge key={stop} variant="soft">{stop}</Badge>)}
                  </div>
                  <div className="info-card__foot">
                    <Button size="sm" variant="ghost" onClick={handleSave} icon={<Icon name="bookmark" size={14} />} iconPosition="left">{t('Save this route')}</Button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showLoginPrompt && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={t('Sign in required')} onClick={() => setShowLoginPrompt(false)}>
          <div className="card card--pad-lg planner__modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="h-card" style={{ marginBottom: 8 }}>{t('Sign in required')}</h2>
            <p className="muted" style={{ marginBottom: 20 }}>{t('You need to be signed in to save itineraries for future reference.')}</p>
            <div className="hstack" style={{ gap: 10 }}>
              <Button onClick={() => navigate('/login', { state: { from: '/travel-planner' } })} icon={<Icon name="log-in" size={16} />} iconPosition="left">{t('Sign in')}</Button>
              <Button variant="ghost" onClick={() => setShowLoginPrompt(false)}>{t('Cancel')}</Button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast" role="status">{toast}</div>}
    </>
  );
}
